import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProStudioNodeDataStore } from '@pypestream/pro-studio-collab-client';

import { isCollabRoomId } from './collab-live-source';
import { loadCollabClient } from './load-collab-client';

/**
 * One live collab session per open draft (PSTUDIO-4449).
 *
 * The session is the collab client's MobX store over two Yjs rooms: the draft's
 * version room and the user's view-settings room. This hook only opens and
 * closes it; the Workflows table reads and writes through it via the table
 * feed (PSTUDIO-4450 / PSTUDIO-4451). It is called once, by
 * `CollabSessionHost` in the app shell, so a page switch inside the solution
 * keeps the session and its undo history.
 *
 * Lifecycle, all through one effect keyed on the identity:
 * - every id valid → load the client chunk, then connect;
 * - any input changes (draft switch, leaving the solution) or the host
 *   unmounts → the previous store is disconnected before anything else is
 *   handed out;
 * - `reload()` → disconnect and connect a new store. The client has no way to
 *   recover a refused room, so a fresh store is the only retry there is.
 */
export type CollabSessionParams = {
  url: string | null;
  /** The solution's database id — not the `${customerId}.${botName}` key. */
  solutionId: string | undefined;
  solutionVersionId: string | undefined;
  userId: string | undefined;
};

export type CollabSession = {
  store: ProStudioNodeDataStore | null;
  /** The client chunk could not be loaded, so there is no store to show. */
  failed: boolean;
  /** About to connect: the client is loading, or a reload has not produced its store yet. */
  pending: boolean;
  reload: () => void;
};

const isRoomId = isCollabRoomId;

export const useCollabSession = ({
  url,
  solutionId,
  solutionVersionId,
  userId,
}: CollabSessionParams): CollabSession => {
  const [attempt, setAttempt] = useState(0);
  // Every result is stamped with the identity it was opened for. The render
  // that carries a NEW identity runs before the effect that disconnects the old
  // store, so without the stamp it would hand the previous draft's store to the
  // next draft for one pass.
  const key = [solutionId, solutionVersionId, userId, url, attempt].join('|');
  const [result, setResult] = useState<{
    key: string;
    store: ProStudioNodeDataStore | null;
    failed: boolean;
  } | null>(null);

  const willConnect =
    !!url &&
    isRoomId(solutionId) &&
    isRoomId(solutionVersionId) &&
    isRoomId(userId);

  useEffect(() => {
    // Same test as `willConnect`, repeated so TypeScript narrows the ids here.
    if (
      !url ||
      !isRoomId(solutionId) ||
      !isRoomId(solutionVersionId) ||
      !isRoomId(userId)
    ) {
      return;
    }

    let cancelled = false;
    let connected: ProStudioNodeDataStore | null = null;

    // Lazy on purpose: the client bundles the agent compiler, and only a v2
    // draft's solution pages need it — never All Solutions or Project
    // Settings.
    loadCollabClient()
      .then(({ connectProStudioNodeData }) => {
        if (cancelled) return;
        connected = connectProStudioNodeData({
          url,
          solutionId,
          solutionVersionId,
          userId,
          validation: false,
        });
        setResult({ key, store: connected, failed: false });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Live editing: could not load the collab client', error);
        setResult({ key, store: null, failed: true });
      });

    return () => {
      cancelled = true;
      connected?.disconnect();
      setResult(null);
    };
  }, [url, solutionId, solutionVersionId, userId, attempt, key]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  const current = willConnect && result?.key === key ? result : null;
  const store = current?.store ?? null;
  const failed = current?.failed ?? false;
  const pending = willConnect && current === null;

  return useMemo(
    () => ({ store, failed, pending, reload }),
    [store, failed, pending, reload]
  );
};
